import { DocumentTranscriptTextV1 } from '@utils/types/editors/transcripts/transcript.ts';
import {
  LiveSegment,
  LiveUtterance
} from '@utils/types/editors/live-transcript.ts';

export type LiveDraft = {
  segment: LiveSegment;
  /** The last server version, retained while local text or speaker changes exist. */
  serverSegment?: LiveSegment;
  dirty: boolean;
  editVersion: number;
  textDirty?: boolean;
  speakerDirty?: boolean;
  speakerEditVersion?: number;
  conflict?: boolean;
  editorEpoch?: number;
};

export const liveUtteranceText = (utterance: LiveUtterance): string => {
  const text = (leaf: DocumentTranscriptTextV1): string =>
    leaf.children?.length ? leaf.children.map(text).join('') : leaf.text;
  return utterance.children
    .map(paragraph => paragraph.children.map(text).join(''))
    .join('\n');
};

export const mergeLiveSegments = (
  current: LiveDraft[],
  incoming: LiveSegment[]
): LiveDraft[] => {
  // Most tail polls are empty. Keep their cost independent of event length,
  // and preserve React state identity instead of rebuilding the whole ledger.
  if (!incoming.length) return current;
  const rows = new Map(current.map(row => [row.segment.segment_id, row]));
  let changed = false;
  for (const segment of incoming) {
    const previous = rows.get(segment.segment_id);
    if (previous?.dirty) {
      if (
        !previous.serverSegment &&
        previous.segment.revision === segment.revision
      ) {
        rows.set(segment.segment_id, { ...previous, serverSegment: segment });
        changed = true;
        continue;
      }
      if (
        previous.segment.revision !== segment.revision &&
        !previous.conflict
      ) {
        rows.set(segment.segment_id, { ...previous, conflict: true });
        changed = true;
      }
      continue;
    }
    if (previous && previous.segment.revision === segment.revision) continue;
    rows.set(segment.segment_id, {
      segment,
      dirty: false,
      editVersion: 0,
      editorEpoch: previous ? (previous.editorEpoch ?? 0) + 1 : 0
    });
    changed = true;
  }
  if (!changed) return current;
  return [...rows.values()].sort(
    (a, b) => a.segment.sequence - b.segment.sequence
  );
};

export const acceptLiveSave = (
  current: LiveDraft[],
  sent: Map<string, number>,
  saved: LiveSegment[]
): LiveDraft[] =>
  current.map(row => {
    const server = saved.find(
      segment => segment.segment_id === row.segment.segment_id
    );
    if (!server) return row;
    const matches = row.editVersion === sent.get(row.segment.segment_id);
    return {
      ...row,
      segment: matches
        ? server
        : {
            ...row.segment,
            revision: server.revision,
            utterance: {
              ...row.segment.utterance,
              segment_revision: server.revision
            }
          },
      serverSegment: matches ? undefined : server,
      dirty: !matches,
      textDirty: matches ? false : row.textDirty,
      speakerDirty: matches ? false : row.speakerDirty,
      conflict: false
    };
  });

export const acceptLiveSpeakerSave = (
  current: LiveDraft[],
  id: string,
  sentVersion: number,
  server: LiveSegment
): LiveDraft[] =>
  current.map(row => {
    if (row.segment.segment_id !== id) return row;
    const speakerSaved = row.speakerEditVersion === sentVersion;
    const speakerDirty = speakerSaved ? false : row.speakerDirty === true;
    const textDirty = row.textDirty === true;
    return {
      ...row,
      segment: {
        ...row.segment,
        revision: server.revision,
        utterance: {
          ...row.segment.utterance,
          segment_revision: server.revision
        }
      },
      serverSegment: textDirty || speakerDirty ? server : undefined,
      dirty: textDirty || speakerDirty,
      speakerDirty,
      conflict: false
    };
  });
